import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,  
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import type { AuthResponse } from './types';

interface SignInProps {
  productName: string;
  onSignInSuccess?: (response: AuthResponse) => void;
}

export default function SignIn({ productName, onSignInSuccess }: SignInProps) {
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const onSignIn = async ({ user, password }: { user: string; password: string }) => {
    if (!user || !password) throw new Error('Please enter user and password');
    
    // const awsUrl = 'https://c7h2xebnekef5a33h5ioqlzskm0pjpcb.lambda-url.us-east-1.on.aws/';
    const azureUrl = 'https://spoxmen-signin-ejdkbubna9h8hyfs.westeurope-01.azurewebsites.net/api/SpoxmenSignin'
    const signInResult = await fetch(
      azureUrl,
      {
        method: 'POST',
        mode: 'cors',
        body: JSON.stringify({
          User: user,
          Password: password
        })
      }
    );
    if (signInResult.status === 200) {
      const resultJson = (await signInResult.json()) as Record<string, unknown>;
      onSignInSuccess?.(resultJson);
      navigate('/Energy');
    } else {
      throw new Error('Login not succesful');
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await onSignIn({ user, password });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 440, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <img src="/logo192.png" alt="logo" style={{ height: 48 }} />
          </Box>

          <Typography variant="h5" align="center" sx={{ mb: 1 }}>
            {productName}
          </Typography>
          <Typography variant="body2" align="center" sx={{ mb: 2, color: 'text.secondary' }}>
            Sign in to continue
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2 }} aria-label="Sign in form">
            <TextField
              id="user"
              label="Email or phone"
              type="text"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="you@example.com"
              required
              fullWidth
              autoFocus
            />
            <TextField
              id="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button type="submit" variant="contained" color="primary" disabled={submitting} fullWidth>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </Box>
        </Paper>
      </Box>
  );
}
